import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  ActionType,
  SetStatusActionStatus,
} from "@redotech/redo-return-core-common/return-flow/action-enums";
import { ADD_TAG } from "@redotech/redo-return-ui/flows/rules/add-tag";
import { ASSIGN } from "@redotech/redo-return-ui/flows/rules/assign";
import { AUTO_REPLY } from "@redotech/redo-return-ui/flows/rules/auto-reply";
import {
  ActionState,
  ActionTypeSetup,
  DetailsComponentsForActions,
  MultipleActionsState,
} from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { SET_STATUS } from "@redotech/redo-return-ui/flows/rules/set-status";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { SetStateAction, memo } from "react";
import { AddTagDetails } from "./add-tag";
import { AssignDetails } from "./assign";
import { AutoReplyDetails } from "./auto-reply";
import * as multipleStepsCss from "./multiple-steps.module.css";
import { SetStatusDetails } from "./set-status";

export const MultipleActionsDetails = memo(function MultipleActionsDetails({
  stepSelect,
  state,
  setState,
  flowType,
  step,
  allSteps,
  setStateWithId,
}: StepTypeDetailsProps<MultipleActionsState>) {
  const { t } = useTranslation();
  const handleAddAction = () => {
    setState((oldState) => {
      return {
        ...oldState,
        actions: [
          ...oldState.actions,
          {
            actionTypeSetup: SET_STATUS(SetStatusDetails),
            actionState: SET_STATUS(SetStatusDetails).fromModel({
              type: ActionType.SetStatus,
              status: SetStatusActionStatus.CLOSED,
            }),
          },
        ],
      };
    });
  };
  const handleDeleteAction = (index: number) => {
    setState((oldState) => {
      return {
        ...oldState,
        actions: oldState.actions.filter((_, ind) => ind !== index),
      };
    });
  };
  return (
    <>
      {state.actions.map((action, index) => (
        <>
          <Divider />
          <ActionDetails
            allSteps={allSteps}
            flowType={flowType}
            onDeleteChild={() => handleDeleteAction(index)}
            setState={(updateSingleAction: SetStateAction<ActionState>) => {
              let updatedAction: ActionState;
              if (typeof updateSingleAction === "function") {
                const updateActionState = updateSingleAction(action);
                if (typeof updateActionState.actionState === "function") {
                  const updatedActionState = updateActionState.actionState(
                    action.actionState,
                  );
                  updatedAction = {
                    ...updateActionState,
                    actionState: updatedActionState,
                  };
                } else {
                  updatedAction = updateActionState;
                }
              } else {
                updatedAction = updateSingleAction;
              }
              setState((oldState) => {
                return {
                  ...oldState,
                  actions: oldState.actions.map((act, ind) => {
                    if (index === ind) {
                      return updatedAction;
                    } else {
                      return act;
                    }
                  }),
                };
              });
            }}
            setStateWithId={setStateWithId}
            state={action}
            step={step}
            stepSelect={stepSelect}
          />
        </>
      ))}
      <LabeledInput
        errors={
          state.actions.length === 0
            ? [t("At least one action is required")]
            : []
        }
        label=""
      >
        <RedoButton
          hierarchy="primary"
          onClick={handleAddAction}
          size="md"
          text={t("+ Add action")}
          theme="normal"
        />
      </LabeledInput>
      <LabeledInput errors={[]} label={t("Next step")}>
        {stepSelect({
          value: state.next,
          valueChange: (next) => setState((state) => ({ ...state, next })),
        })}
      </LabeledInput>
    </>
  );
});

const options: ActionTypeSetup<any, any>[] = [
  SET_STATUS(SetStatusDetails),
  ADD_TAG(AddTagDetails),
  AUTO_REPLY(AutoReplyDetails),
  ASSIGN(AssignDetails),
];

const ActionDetails = memo(function ActionDetails({
  stepSelect,
  state,
  setState,
  flowType,
  onDeleteChild,
}: StepTypeDetailsProps<ActionState>) {
  const { t } = useTranslation();
  // TODO: NOT SURE WHAT THE RIGHT CHECK IS
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditSettings = canEditReturnsSettings || canEditSupportSettings;
  const setActionState = useHandler((actionState: any) => {
    setState((state) => ({ ...state, actionState }));
  });
  const handleType = useHandler(
    async (actionType: ActionTypeSetup<any, any>) => {
      setState((state) => ({
        ...state,
        actionTypeSetup: actionType,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        actionState: actionType!.empty,
      }));
    },
  );
  return (
    <>
      <LabeledInput
        label={
          <div className={multipleStepsCss.labelWrapper}>
            <p>{t("Action")}</p>{" "}
            <RedoButton
              aria-label={t("Delete")}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={(event) => {
                onDeleteChild?.();
                event.stopPropagation();
              }}
              size="sm"
              theme="normal"
            />
          </div>
        }
      >
        <SelectDropdown
          disabled={!canEditSettings}
          options={options}
          value={state.actionTypeSetup}
          valueChange={handleType}
        >
          {(action) => action.name}
        </SelectDropdown>
      </LabeledInput>
      {state.actionTypeSetup && (
        <state.actionTypeSetup.Details
          disabled={!canEditSettings}
          flowType={flowType}
          setState={setActionState}
          state={state.actionState}
        />
      )}
    </>
  );
});

export const DetailsForActions: DetailsComponentsForActions = {
  SetStatusDetails,
  AddTagDetails,
  AutoReplyDetails,
  AssignDetails,
};
